import { lazy, Suspense, useCallback } from 'react';
import { Button } from '@webmotors/eleanor-ui';
import { myAdsPageStyles } from './my-ads.styles';
import { useMyAdsPage } from './my-ads.hook';
import { AdsTabs, VehicleCard, PlusIcon, LoadingState, EmptyState, ErrorState } from './components';
import { MY_ADS_PAGE, MY_ADS_TEST_IDS } from './utils';
import { ConfirmationModal } from '@/components/confirmation-modal';
import { ENV } from '@/app/config';

const EditModal = lazy(() =>
	import('./components/edit-modal').then((m) => ({ default: m.EditModal }))
);
const EditMobileView = lazy(() =>
	import('./components/edit-mobile-view').then((m) => ({ default: m.EditMobileView }))
);
const UpgradeModal = lazy(() =>
	import('./components/upgrade-modal').then((m) => ({ default: m.UpgradeModal }))
);

const MyAdsPage = () => {
	const {
		ads,
		isListLoading,
		isTabCountsReady,
		isUpgradeCheckPending,
		isError,
		isEmpty,
		activeTab,
		setActiveTab,
		tabCounts,
		refetch,
		handleAdAction,
		handleMenuAction,
		deactivateModal,
		closeDeactivateModal,
		confirmDeactivate,
		markAsSoldModal,
		closeMarkAsSoldModal,
		confirmMarkAsSold,
		editModal,
		closeEditModal,
		handleEditSuccess,
		upgradeModal,
		closeUpgradeModal,
		handleUpgradeSuccess,
		deleteModal,
		closeDeleteModal,
		confirmDelete,
		isMobile
	} = useMyAdsPage();

	const isListBusy = isListLoading || isUpgradeCheckPending || markAsSoldModal.isLoading;

	const handleCreateAd = useCallback(() => {
		window.open(ENV.URL_SELL, '_blank', 'noopener,noreferrer');
	}, []);

	return (
		<main className={myAdsPageStyles.container} data-testid={MY_ADS_TEST_IDS.PAGE}>
			<section className={myAdsPageStyles.content}>
				<header className={myAdsPageStyles.header}>
					<h1 className={myAdsPageStyles.title}>{MY_ADS_PAGE.TITLE}</h1>
					<div className={myAdsPageStyles.createButtonWrapper}>
						<Button
							variant="secondary"
							size="medium"
							leftIcon={{ component: <PlusIcon /> }}
							onClick={handleCreateAd}
							data-testid={MY_ADS_TEST_IDS.CREATE_BUTTON}
						>
							{MY_ADS_PAGE.CREATE_AD_BUTTON}
						</Button>
					</div>
				</header>

				<div className={myAdsPageStyles.tabsSection}>
					{isTabCountsReady ? (
						<AdsTabs activeTab={activeTab} counts={tabCounts} onTabChange={setActiveTab} />
					) : (
						<LoadingState showTabs showList={false} />
					)}
				</div>

				{isListBusy && <LoadingState showTabs={false} showList />}

				{!isListBusy && isError && <ErrorState onRetry={refetch} />}

				{!isListBusy && isEmpty && <EmptyState />}

				{!isListBusy && !isError && !isEmpty && (
					<div className={myAdsPageStyles.adsList} data-testid={MY_ADS_TEST_IDS.ADS_LIST}>
						{ads.map((ad) => (
							<VehicleCard
								key={ad.id}
								ad={ad}
								layout={isMobile ? 'mobile' : 'desktop'}
								onAction={handleAdAction}
								onMenuAction={handleMenuAction}
							/>
						))}
					</div>
				)}
			</section>

			<ConfirmationModal
				dataTestId={MY_ADS_TEST_IDS.DEACTIVATE_MODAL}
				isOpen={deactivateModal.isOpen}
				onClose={closeDeactivateModal}
				title={MY_ADS_PAGE.DEACTIVATE_MODAL.TITLE}
				confirmLabel={MY_ADS_PAGE.DEACTIVATE_MODAL.CONFIRM_LABEL}
				onConfirm={confirmDeactivate}
				isLoading={deactivateModal.isLoading}
			>
				<p className={myAdsPageStyles.modalDescription}>
					{MY_ADS_PAGE.DEACTIVATE_MODAL.DESCRIPTION}
				</p>
			</ConfirmationModal>

			<ConfirmationModal
				dataTestId={MY_ADS_TEST_IDS.MARK_AS_SOLD_MODAL}
				isOpen={markAsSoldModal.isOpen}
				onClose={closeMarkAsSoldModal}
				title={MY_ADS_PAGE.MARK_AS_SOLD_MODAL.TITLE}
				confirmLabel={MY_ADS_PAGE.MARK_AS_SOLD_MODAL.CONFIRM_LABEL}
				onConfirm={confirmMarkAsSold}
				isLoading={markAsSoldModal.isLoading}
			>
				<p className={myAdsPageStyles.modalDescription}>
					{MY_ADS_PAGE.MARK_AS_SOLD_MODAL.DESCRIPTION}
				</p>
			</ConfirmationModal>

			<ConfirmationModal
				dataTestId={MY_ADS_TEST_IDS.DELETE_MODAL}
				isOpen={deleteModal.isOpen}
				onClose={closeDeleteModal}
				title={MY_ADS_PAGE.DELETE_MODAL.TITLE}
				confirmLabel={MY_ADS_PAGE.DELETE_MODAL.CONFIRM_LABEL}
				onConfirm={confirmDelete}
				isLoading={deleteModal.isLoading}
			>
				<p className={myAdsPageStyles.modalDescription}>
					{MY_ADS_PAGE.DELETE_MODAL.DESCRIPTION}
				</p>
			</ConfirmationModal>

			<Suspense fallback={null}>
				{editModal.isOpen &&
					(isMobile ? (
						<EditMobileView
							isOpen={editModal.isOpen}
							adId={editModal.adId}
							vehicleType={editModal.vehicleType}
							onClose={closeEditModal}
							onSuccess={handleEditSuccess}
						/>
					) : (
						<EditModal
							isOpen={editModal.isOpen}
							adId={editModal.adId}
							vehicleType={editModal.vehicleType}
							onClose={closeEditModal}
							onSuccess={handleEditSuccess}
						/>
					))}

				{upgradeModal.isOpen && (
					<UpgradeModal
						isOpen={upgradeModal.isOpen}
						adId={upgradeModal.adId}
						vehicleType={upgradeModal.vehicleType}
						listingPhotoUrl={upgradeModal.photoUrl}
						listingAd={upgradeModal.listingAd}
						onClose={closeUpgradeModal}
						onSuccess={handleUpgradeSuccess}
					/>
				)}
			</Suspense>
		</main>
	);
};

MyAdsPage.displayName = 'MyAdsPage';

export { MyAdsPage };
